import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import {
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonContent,
    IonFooter,
    IonHeader,
    IonIcon,
    IonModal,
    IonSpinner,
    IonText,
    IonTitle,
    IonToolbar,
    useIonRouter,
} from "@ionic/react";
import { useHasFeatureAccess, useProfile } from "../../components/hooks";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import {
    CameraPreview,
    CameraPreviewOptions,
} from "@capacitor-community/camera-preview";
import { Capacitor } from "@capacitor/core";
import { Player, SelectedTeam } from "refsix-js-models";
import { useMatch } from "../../components/hooks/match";
import { useTranslation } from "react-i18next";
import "./teamSheetScan.css";
import { camera, closeOutline, imageOutline } from "ionicons/icons";
import {
    trackEvent,
    trackProTeaser,
} from "../../services/analytics/analyticsService";
import {
    cleanPlayers,
    postTeamSheetScan,
} from "../../utils/teamSheetScanHelper";
import { getLocale } from "../../utils/localeUtils";
import { updateMatch } from "../../services/matchService";
import { routes } from "../../route/constants";

export interface TeamSheetScanProps {
    matchId?: string;
    teamSide?: SelectedTeam.home | SelectedTeam.away;
    isOpen: boolean;
    onSuccess?: () => void;
    onDismiss?: () => void;
}

export const TeamSheetScan: FC<TeamSheetScanProps> = ({
    matchId,
    teamSide,
    isOpen,
    onSuccess,
    onDismiss,
}) => {
    const { t } = useTranslation();
    const [scanningTeamSheet, setScanningTeamSheet] = useState<boolean>(false);
    const [currentImage, setCurrentImage] = useState<string>(""); // base64 encoded image url
    const [scannerError, setScannerError] = useState<boolean>(false);
    const [cameraStarted, setCameraStarted] = useState<boolean>(false);
    const [capturedBase64, setCapturedBase64] = useState<string | null>(null);
    const modalElement = useRef<HTMLIonModalElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const mountedRef = useRef(true);

    const match = useMatch(matchId);
    const profile = useProfile();
    const hasAccess = useHasFeatureAccess("teamSheetScan");
    const route = useIonRouter();

    const isNative = Capacitor.isNativePlatform();

    // Web-specific camera functions
    const startWebCamera = async () => {
        // Stop existing stream first to prevent leaks
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "environment" }, // Prefer back camera on mobile browsers
                audio: false,
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }
            setCameraStarted(true);
        } catch (error) {
            console.error("Error accessing webcam:", error);
            setScannerError(true);
        }
    };

    const stopWebCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    const captureWebPhoto = (): string | null => {
        if (!videoRef.current) return null;
        if (!videoRef.current.videoWidth || !videoRef.current.videoHeight)
            return null;

        const canvas = document.createElement("canvas");
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;

        ctx.drawImage(videoRef.current, 0, 0);
        return canvas.toDataURL("image/jpeg", 0.9).split(",")[1]; // base64 without prefix
    };

    const stopCamera = useCallback(async () => {
        if (!isNative) {
            stopWebCamera();
            setCameraStarted(false);
            return;
        }
        if (cameraStarted) {
            try {
                await CameraPreview.stop();
                setCameraStarted(false);
            } catch (error) {
                console.error("Error stopping camera:", error);
            }
        }
    }, [cameraStarted, isNative]);

    const startCamera = useCallback(async () => {
        if (!isNative) {
            await startWebCamera();
            return;
        }

        // Calculate camera position from screen dimensions
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;

        // Approximate header height (toolbar ~56px + safe area)
        const headerHeight = 100;

        // Approximate footer height (controls ~112px + safe area)
        const footerHeight = 130;

        // Available height for camera
        const availableHeight = screenHeight - headerHeight - footerHeight;

        // Camera dimensions (3:4 portrait aspect ratio, full width)
        const cameraWidth = screenWidth;
        const cameraHeight = cameraWidth * (4 / 3); // 3:4 portrait aspect ratio

        // Center vertically in available space
        const y = headerHeight + (availableHeight - cameraHeight) / 2;

        try {
            const options: CameraPreviewOptions = {
                position: "rear",
                enableZoom: true,
                disableAudio: true,
                toBack: false, // Render in front of webview
                x: 0,
                y: Math.round(y),
                width: Math.round(cameraWidth),
                height: Math.round(cameraHeight),
            };
            await CameraPreview.start(options);
            setCameraStarted(true);
        } catch (error) {
            console.error("Error starting camera:", error);
            setScannerError(true);
        }
    }, [isNative]);

    const selectFromGalleryOrCamera = async () => {
        try {
            const image = await Camera.getPhoto({
                quality: 90,
                allowEditing: false,
                resultType: CameraResultType.Base64,
                correctOrientation: true,
                width: 2048,
                height: 2048,
                source: CameraSource.Prompt,
                promptLabelHeader: `AI ${t("fixture.Fixture.teamsheet")}`,
                promptLabelPhoto: t("teamSheetScan.fromPhotos"),
                promptLabelPicture: t("teamSheetScan.takePhoto"),
            });

            if (image.base64String) {
                await stopCamera();
                await processImage(image.base64String);
            }
        } catch (error) {
            console.error("Error selecting from gallery/camera:", error);
            // User cancelled or error occurred
        }
    };

    const selectFromGallery = async () => {
        // Stop camera before opening gallery
        await stopCamera();

        try {
            const image = await Camera.getPhoto({
                quality: 90,
                allowEditing: false,
                resultType: CameraResultType.Base64,
                correctOrientation: true,
                width: 2048,
                height: 2048,
                source: CameraSource.Photos,
            });

            if (image.base64String) {
                await processImage(image.base64String);
            }
        } catch (error) {
            console.error("Error selecting from gallery:", error);
            // User cancelled - restart camera if still mounted
            if (mountedRef.current) {
                await startCamera();
            }
        }
    };

    const capturePhoto = async () => {
        try {
            let base64: string | null = null;

            if (isNative) {
                const result = await CameraPreview.capture({ quality: 90 });
                base64 = result.value;
            } else {
                base64 = captureWebPhoto();
            }

            if (base64) {
                await stopCamera();
                await processImage(base64);
            }
        } catch (error) {
            console.error("Error capturing photo:", error);
            setScannerError(true);
        }
    };

    const processImage = async (base64String: string) => {
        if (!match || !teamSide) return;

        try {
            setScanningTeamSheet(true);
            setScannerError(false);
            setCapturedBase64(base64String);
            setCurrentImage(`data:image/jpeg;base64,${base64String}`);

            console.log(
                `Image size: ${(base64String.length || 1) / 1024 / 1024} MB`
            );

            const response = await postTeamSheetScan({
                image: base64String,
                locale: getLocale(),
                country: profile?.country || "",
                startingPlayersCount: match.teamSize || 11,
                benchPlayersCount: match.subsNo || 5,
                matchId: match._id,
                teamSide: teamSide,
            });

            if (!response || !response.data) {
                throw new Error("No response or data");
            }

            const scannedPlayers: Player[] = response.data;
            console.log("players", scannedPlayers);

            const cleanedPlayers = cleanPlayers(scannedPlayers);
            console.log("cleanedPlayers", cleanedPlayers);

            const selectedTeamName =
                teamSide === SelectedTeam.home
                    ? match.homeTeam
                    : match.awayTeam;

            const updatedPlayers = {
                ...match.players,
                [selectedTeamName]: cleanedPlayers,
            };

            const updatedMatch = {
                ...match,
                players: updatedPlayers,
            };

            await updateMatch(updatedMatch);

            trackEvent("ScanTeamSheetSuccess", {
                matchId: match._id,
                side: teamSide,
            });

            setScanningTeamSheet(false);
            onSuccess && onSuccess();
        } catch (error) {
            console.error(error);
            trackEvent("ScanTeamSheetError", {
                matchId: match?._id,
                teamSide,
                error: JSON.stringify(error),
            });
            setScanningTeamSheet(false);
            setScannerError(true);
        }
    };

    const handleRetry = async () => {
        setScannerError(false);
        setCurrentImage("");
        setCapturedBase64(null);
        await startCamera();
    };

    const handleDismiss = async () => {
        try {
            await stopCamera();
        } catch (error) {
            console.error("Error stopping camera during dismiss:", error);
        } finally {
            setScanningTeamSheet(false);
            setCurrentImage("");
            setCapturedBase64(null);
            setScannerError(false);
            setCameraStarted(false);
            onDismiss && onDismiss();
        }
    };

    const handleModalPresent = async () => {
        if (hasAccess && !cameraStarted && !scanningTeamSheet) {
            await startCamera();
        }
        if (!hasAccess) {
            trackProTeaser("aiTeamSheet");
        }
    };

    // Track component mounted state
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Cleanup camera on unmount
    useEffect(() => {
        return () => {
            if (cameraStarted) {
                if (isNative) {
                    CameraPreview.stop().catch(console.error);
                } else {
                    stopWebCamera();
                }
            }
        };
    }, [cameraStarted, isNative]);

    if (!match || !teamSide) {
        return null;
    }

    // Show container when user has access and not processing/error
    const showCameraContainer =
        hasAccess && !scanningTeamSheet && !scannerError;
    // Track if camera is actually running (for UI feedback if needed)
    const showCameraView = showCameraContainer && cameraStarted;
    const showProcessingView =
        hasAccess && scanningTeamSheet && currentImage && !scannerError;
    const showErrorView = hasAccess && scannerError;
    const showProTeaser = !hasAccess;

    return (
        <IonModal
            isOpen={isOpen}
            ref={modalElement}
            onDidDismiss={handleDismiss}
            onDidPresent={handleModalPresent}
            className="team-sheet-scan-modal"
        >
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="end">
                        <IonButton onClick={handleDismiss}>
                            <IonIcon slot="icon-only" icon={closeOutline} />
                        </IonButton>
                    </IonButtons>
                    <IonTitle>
                        <span className="rainbox-text">AI</span>{" "}
                        {t("fixture.Fixture.teamsheet")}
                    </IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding team-sheet-scan-content">
                {/* Web camera preview */}
                {!isNative && showCameraContainer && (
                    <div className="web-camera-container">
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            className="web-camera-video"
                        />
                    </div>
                )}

                {/* Processing View */}
                {showProcessingView && (
                    <>
                        <div className="border-wrapper">
                            <div className="image-container">
                                <img src={currentImage} alt="Team sheet" />
                            </div>
                            <div
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    textAlign: "center",
                                    marginTop: "50%",
                                }}
                            >
                                <IonSpinner
                                    name="dots"
                                    style={{
                                        width: 100,
                                        height: 100,
                                    }}
                                    duration={1500}
                                ></IonSpinner>
                            </div>
                        </div>

                        <IonCard style={{ margin: "20px 0" }}>
                            <IonCardContent>
                                <IonText style={{ color: "white" }}>
                                    {t("teamSheetScan.disclaimer")}
                                </IonText>

                                <p style={{ marginTop: 20 }}>
                                    <small>
                                        {t(
                                            "teamSheetScan.disclaimerPhotoStorage"
                                        )}
                                    </small>
                                </p>
                            </IonCardContent>
                        </IonCard>
                    </>
                )}

                {/* Error View */}
                {showErrorView && (
                    <IonCard>
                        <IonCardContent>
                            <h2>{t("general.errorTryAgain")}</h2>
                            <div className="ion-text-center">
                                <IonButton
                                    style={{ marginTop: 20 }}
                                    data-testid="scanTeamSheetButton"
                                    onClick={handleRetry}
                                    className="rainbow-button"
                                >
                                    <IonIcon
                                        icon={camera}
                                        style={{ fontSize: 28 }}
                                    />
                                </IonButton>
                            </div>
                        </IonCardContent>
                    </IonCard>
                )}

                {/* Pro Teaser View */}
                {showProTeaser && (
                    <IonCard>
                        <IonCardContent>
                            <h2 style={{ color: "white", marginBottom: 20 }}>
                                {t("general.proFeature")}
                            </h2>

                            {/* Vimeo player */}
                            <div
                                style={{
                                    position: "relative",
                                    width: "80%",
                                    margin: "0 auto 10px",
                                }}
                            >
                                <div
                                    style={{
                                        padding: "177.78% 0 0 0",
                                        position: "relative",
                                    }}
                                >
                                    <iframe
                                        src="https://player.vimeo.com/video/1056009294?h=29ae7bc3d8&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&autoplay=1&muted=1&background=1"
                                        frameBorder="0"
                                        allow="autoplay; fullscreen; picture-in-picture"
                                        allowFullScreen
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "100%",
                                            height: "100%",
                                        }}
                                        title={`AI ${t(
                                            "fixture.Fixture.teamsheet"
                                        )}`}
                                    ></iframe>
                                </div>
                                <script src="https://player.vimeo.com/api/player.js"></script>
                            </div>
                            <p style={{ marginTop: 20 }}>
                                <IonText style={{ color: "white" }}>
                                    {t(
                                        "upgrade.proFeaturesContent.teamSheetScan"
                                    )}
                                </IonText>
                            </p>
                            <div className="ion-text-center">
                                <IonButton
                                    style={{ marginTop: 20 }}
                                    data-testid="scanTeamSheetButton"
                                    onClick={() => {
                                        route.push(`${routes.upgrade}/pro`);
                                        onDismiss && onDismiss();
                                    }}
                                >
                                    {t("menu.upgrade")}
                                </IonButton>
                            </div>
                        </IonCardContent>
                    </IonCard>
                )}
            </IonContent>

            {/* Fixed footer with camera controls - only show when camera is active */}
            {showCameraContainer && (
                <IonFooter className="camera-footer">
                    <div className="camera-controls">
                        <IonButton
                            className="gallery-button"
                            fill="clear"
                            onClick={selectFromGallery}
                        >
                            <IonIcon icon={imageOutline} slot="icon-only" />
                        </IonButton>
                        <IonButton
                            className="shutter-button"
                            shape="round"
                            onClick={capturePhoto}
                        >
                            <IonIcon icon={camera} slot="icon-only" />
                        </IonButton>
                    </div>
                </IonFooter>
            )}
        </IonModal>
    );
};
